// SAB-1045: the "W / <clear day>" designation, rendered from ONE place.
//
// The server decides WHETHER a player is on waivers and WHEN they clear
// (get_nfl_player_leaderboard.waiver_state / waiver_clears_at, derived from the same
// nfl_waiver_settings() the nfl-add-drop refusal reads). This module only formats that
// instant. It deliberately contains no rule: no drop-lock arithmetic, no window
// comparison, nothing that could disagree with the server.
//
// SAB-1035 removed a client-side recomputation of the waiver rules because the copies
// drifted and the UI offered adds the server refused. Keeping the formatter rule-free is
// what stops a badge putting that back.

// SAB-1142: 'played' — his game this week has kicked off, so he is claim-only until the
// WEEKLY run. Added to the server's vocabulary in the same ticket; without it the row read
// as a free agent, the overlay opened in add mode, and the server's PLAYER_ON_WAIVERS
// refusal surfaced as a red error with no bid input.
export type WaiverState = 'post_draft' | 'dropped' | 'played' | null | undefined

export interface WaiverInfo {
  waiver_state?: WaiverState
  waiver_clears_at?: string | null
}

/** True when the row carries a server-derived waiver designation. */
export function isOnWaivers(p: WaiverInfo | null | undefined): boolean {
  return !!p && (p.waiver_state === 'post_draft' || p.waiver_state === 'dropped' || p.waiver_state === 'played')
}

/**
 * The day part of the badge, in the VIEWER's timezone.
 *
 * Rendered client-side on purpose: the clear instant is absolute, but "which day is
 * that" is not. A server-formatted weekday would be the server's day, and a manager in
 * Honolulu reading a UTC "Fri" for a Thursday-evening clear is the same class of bug as
 * the age gate that failed open on a UTC box.
 *
 * Within a week we show the weekday, which is what the Sleeper pattern shows and what a
 * manager actually plans around. Beyond that a weekday is ambiguous ("Fri" — which one?),
 * so it becomes a date.
 */
export function waiverClearLabel(clearsAt: string | null | undefined, now: Date = new Date()): string | null {
  if (!clearsAt) return null
  const d = new Date(clearsAt)
  if (Number.isNaN(d.getTime())) return null

  // Calendar-day difference in LOCAL time, not a division of the millisecond gap:
  // 23:30 tonight and 00:30 tomorrow are 1 hour apart and two different days, and
  // Math.floor(ms / 86400000) calls that zero.
  const startOfDay = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime()
  const days = Math.round((startOfDay(d) - startOfDay(now)) / 86400000)

  if (days <= 0) return 'today'
  if (days === 1) return 'tmrw'
  if (days < 7) return d.toLocaleDateString(undefined, { weekday: 'short' })
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

/** The list-row form: "W / Fri". Null when the player is not on waivers. */
export function waiverBadgeText(p: WaiverInfo | null | undefined, now?: Date): string | null {
  if (!isOnWaivers(p)) return null
  const day = waiverClearLabel(p?.waiver_clears_at, now)
  // A waiver player with no clear instant still gets a badge -- "on waivers" is the
  // load-bearing half. Dropping the badge entirely because the date is missing would
  // show a +Add the server then refuses.
  return day ? `W / ${day}` : 'W'
}

/** The detail form: "W (Fri)". */
export function waiverChipText(p: WaiverInfo | null | undefined, now?: Date): string | null {
  if (!isOnWaivers(p)) return null
  const day = waiverClearLabel(p?.waiver_clears_at, now)
  return day ? `W (${day})` : 'W'
}

/**
 * The AVAILABILITY label — the same question "can I have this player", answered from the
 * same field the badge reads.
 *
 * SAB-1045 follow-up: the player-detail card derived this from ownership alone
 * (`!ownerTeamName`), so an unrostered player on waivers read "Free Agent" in the corner
 * while the chip beside their name said "W (Fri)". One card, two answers, and the wrong
 * one was the encouraging one.
 *
 * Returns null when the player is NOT on waivers, so the caller falls through to its own
 * owned/free-agent handling rather than this module guessing at ownership it cannot see.
 */
export function waiverAvailabilityLabel(p: WaiverInfo | null | undefined, now?: Date): string | null {
  if (!isOnWaivers(p)) return null
  const day = waiverClearLabel(p?.waiver_clears_at, now)
  // No day is still "on waivers" -- the load-bearing half. Falling back to "Free Agent"
  // because the date is missing would restate the contradiction this function removes.
  return day ? `On Waivers · clears ${day}` : 'On Waivers'
}

/** Tooltip / aria text. Says the rule, not just the day. */
export function waiverBadgeTitle(p: WaiverInfo | null | undefined, now?: Date): string {
  const day = waiverClearLabel(p?.waiver_clears_at, now)
  const when = day === 'today' ? 'today' : day === 'tmrw' ? 'tomorrow' : day ? `on ${day}` : 'soon'
  return p?.waiver_state === 'post_draft'
    ? `On waivers until the post-draft window closes ${when} — place a claim`
    : `On waivers ${when} — place a claim`
}
